import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { isNative } from '../lib/platform';

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_PREFERENCES_KEY = 'cookie-preferences';
const CONSENT_RECORD_KEY = 'cookie-consent-record';
const CURRENT_CONSENT_VERSION = '1.0';
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface ConsentRecord {
  status: 'accepted' | 'declined';
  preferences: CookiePreferences;
  timestamp: string;
  consentVersion: string;
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  hasConsented: boolean;
  bannerVisible: boolean;
  saveConsent: (preferences: CookiePreferences) => void;
  reopenBanner: () => void;
  closeBanner: () => void;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

function loadConsentRecord(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_RECORD_KEY);
    if (!raw) {
      // Migrate from old format if present
      const legacyConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (legacyConsent) {
        const legacyPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
        const prefs = legacyPrefs ? JSON.parse(legacyPrefs) : defaultPreferences;
        const record: ConsentRecord = {
          status: legacyConsent as 'accepted' | 'declined',
          preferences: prefs,
          timestamp: new Date().toISOString(),
          consentVersion: CURRENT_CONSENT_VERSION,
        };
        localStorage.setItem(CONSENT_RECORD_KEY, JSON.stringify(record));
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        localStorage.removeItem(COOKIE_PREFERENCES_KEY);
        return record;
      }
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isConsentExpired(record: ConsentRecord): boolean {
  if (record.consentVersion !== CURRENT_CONSENT_VERSION) return true;

  const consentDate = new Date(record.timestamp).getTime();
  if (isNaN(consentDate)) return true;

  return Date.now() - consentDate > CONSENT_MAX_AGE_MS;
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const prevPreferencesRef = useRef<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Cookie consent is web-only; native apps do not use tracking cookies.
    if (isNative()) {
      setHasConsented(true);
      setBannerVisible(false);
      return;
    }

    const record = loadConsentRecord();

    if (!record || isConsentExpired(record)) {
      if (record) {
        localStorage.removeItem(CONSENT_RECORD_KEY);
      }
      const timer = setTimeout(() => setBannerVisible(true), 1000);
      return () => clearTimeout(timer);
    }

    setPreferences(record.preferences);
    prevPreferencesRef.current = record.preferences;
    setHasConsented(true);
  }, []);

  const saveConsent = useCallback((newPreferences: CookiePreferences) => {
    const prev = prevPreferencesRef.current;

    const record: ConsentRecord = {
      status: 'accepted',
      preferences: newPreferences,
      timestamp: new Date().toISOString(),
      consentVersion: CURRENT_CONSENT_VERSION,
    };
    localStorage.setItem(CONSENT_RECORD_KEY, JSON.stringify(record));

    // Fire revocation events for categories that were enabled and are now disabled
    const categories: (keyof CookiePreferences)[] = ['analytics', 'marketing'];
    for (const cat of categories) {
      if (prev[cat] && !newPreferences[cat]) {
        window.dispatchEvent(new CustomEvent('consent-revoked', { detail: { category: cat } }));
      }
    }

    setPreferences(newPreferences);
    prevPreferencesRef.current = newPreferences;
    setHasConsented(true);
    setBannerVisible(false);
  }, []);

  const reopenBanner = useCallback(() => {
    setBannerVisible(true);
  }, []);

  const closeBanner = useCallback(() => {
    setBannerVisible(false);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{ preferences, hasConsented, bannerVisible, saveConsent, reopenBanner, closeBanner }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextType {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return ctx;
}

/**
 * Runs an effect only when the user has consented to a specific cookie category.
 * The effect's cleanup runs when consent is revoked for that category.
 */
export function useConsentGatedEffect(
  category: keyof CookiePreferences,
  effect: () => (() => void) | void,
  deps: React.DependencyList = [],
) {
  const { preferences, hasConsented } = useCookieConsent();
  const cleanupRef = useRef<(() => void) | void>(undefined);

  useEffect(() => {
    if (hasConsented && preferences[category]) {
      cleanupRef.current = effect();
    }

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConsented, preferences[category], ...deps]);

  // Listen for revocation events to trigger cleanup immediately
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.category === category && cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
    };
    window.addEventListener('consent-revoked', handler);
    return () => window.removeEventListener('consent-revoked', handler);
  }, [category]);
}
